import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Eye, Trash2 } from "lucide-react";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import {
  listAllCollaborations,
  deleteCollaborationAdmin,
  AdminCollaborationSummary,
} from "../../../api/admin/collaborations";

import { useAuth } from "../../../context/AuthContext";
import Header from "../../../components/common/Header";
import TablePagination from "../../../components/common/TablePagination";
import FlashMessage from "../../../components/common/FlashMessage";
import { useFlashMessage } from "../../../hooks/useFlashMessage";

const RequestsTableAdmin: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getCsrfToken, user, getUserIdFromToken } = useAuth();
  const { message, type, showMessage, clearMessage } = useFlashMessage(5000);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    null
  );
  const [requests, setRequests] = useState<AdminCollaborationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const mapFrontToPt = useCallback((front: string): FrontStatus => {
    switch (front) {
      case "pending":
        return "Em analise";
      case "approved":
        return "Aprovada";
      case "rejected":
        return "Rejeitada";
      case "correction":
        return "Algo a corrigir";
      default:
        return "Em analise";
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const currentUserId = getUserIdFromToken();
        if (!currentUserId) throw new Error("Usuário não autenticado.");
        const response = await listAllCollaborations(getCsrfToken(), { user_id: currentUserId }, currentPage, pageSize);
        const filteredRaw = currentUserId
          ? response.items.filter((c) => c.user?.user_id === currentUserId)
          : response.items;
        // Sort by most recent (descending by colaboracao_id)
        const sorted = [...filteredRaw].sort(
          (a, b) => b.colaboracao_id - a.colaboracao_id
        );
        const filtered = sorted.map((c) => ({
          ...c,
          status: mapFrontToPt(c.status),
        }));
        if (mounted) {
          setRequests(filtered as any);
          setTotal(response.total);
          setTotalPages(response.totalPages);
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || "Erro ao carregar colaborações");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [getCsrfToken, getUserIdFromToken, mapFrontToPt, currentPage, pageSize]);

  type FrontStatus =
    | "Em analise"
    | "Aprovada"
    | "Rejeitada"
    | "Algo a corrigir";
  const getAvailableActions = (status: FrontStatus) => {
    switch (status) {
      case "Em analise":
        return {
          canEdit: true,
          canView: false,
          canDelete: true,
        };
      case "Aprovada":
        return {
          canEdit: false,
          canView: true,
          canDelete: false,
        };
      case "Rejeitada":
        return {
          canEdit: false,
          canView: true,
          // Allow deletion when a collaboration was rejected so the owner can remove it
          canDelete: true,
        };
      case "Algo a corrigir":
        return {
          canEdit: true,
          canView: false,
          canDelete: false,
        };
      default:
        return {
          canEdit: false,
          canView: false,
          canDelete: false,
        };
    }
  };

  const castStatus = (s: string): FrontStatus => {
    if (
      s === "Em analise" ||
      s === "Aprovada" ||
      s === "Rejeitada" ||
      s === "Algo a corrigir"
    )
      return s;
    return "Em analise";
  };

  const handleEdit = (id: number) => {
    navigate(`/solicitacao-colaboracao-admin/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/colaboracao-admin/${id}`);
  };

  const handleDelete = (id: number) => {
    setSelectedRequestId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedRequestId) return;
    try {
      const token = getCsrfToken();
      if (token) await deleteCollaborationAdmin(selectedRequestId, token);
      setRequests((prev) =>
        prev.filter((r) => r.colaboracao_id !== selectedRequestId)
      );
      showMessage("Colaboração excluída com sucesso", "success");
    } catch (e) {
      console.error(e);
      showMessage("Erro ao excluir colaboração", "error");
      setError("Erro ao excluir colaboração");
    }
    setShowDeleteModal(false);
    setSelectedRequestId(null);
  };

  const visibleRequests = requests.filter((request) => (!statusFilter || request.status === mapFrontToPt(statusFilter)) && (request.nome_especie || "").toLowerCase().includes(searchTerm.trim().toLowerCase()));

  return (
    <div className="account-profile-page account-table-page">
      <Header />
      {message && (
        <div className="container mt-3">
          <FlashMessage message={message} type={type} onClose={clearMessage} />
        </div>
      )}
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar
            activeItem="solicitacoes"
            avatar={avatar}
            name={user?.nome_completo || "Administrador"}
          />
        </div>
        <main className="account-profile-content">
          <div className="account-page-heading"><h1>Minhas solicitações</h1></div>
          <section className="account-table-panel" aria-label="Minhas solicitações">
            <div className="account-table-toolbar">
              <label>Pesquisar nesta página
                <input className="form-control" value={searchTerm} placeholder="Espécie" onChange={(event) => setSearchTerm(event.target.value)} />
              </label>
              <label>Status nesta página
                <select className="form-select" value={statusFilter || ""} onChange={(event) => setStatusFilter(event.target.value || null)}>
                  <option value="">Todos</option><option value="pending">Em análise</option><option value="approved">Aprovada</option><option value="rejected">Rejeitada</option><option value="correction">Algo a corrigir</option>
                </select>
              </label>
            </div>
            <div className="table-responsive" role="region" aria-label="Tabela de minhas solicitações" tabIndex={0}>
          {/* Tabela */}
          <table className="table">
            <thead>
              <tr>
                <th>Status</th>
                <th>ID</th>
                <th>Espécie</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4}>Carregando...</td>
                </tr>
              )}
              {error && !loading && (
                <tr>
                  <td colSpan={4} className="text-danger">
                    {error}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                visibleRequests
                  .map((request) => {
                    const statusPt = castStatus(request.status as any);
                    const actions = getAvailableActions(statusPt);
                    return (
                      <tr key={request.colaboracao_id}>
                        <td>
                          <span className={`account-status ${statusPt === "Aprovada" ? "is-approved" : statusPt === "Rejeitada" ? "is-rejected" : statusPt === "Algo a corrigir" ? "is-correction" : "is-pending"}`}>
                            {statusPt === "Em analise" ? "Em análise" : statusPt}
                          </span>
                        </td>
                        <td>#{request.colaboracao_id}</td>
                        <td>{request.nome_especie || "—"}</td>
                        <td>
                          <div className="action-buttons d-flex justify-content-center gap-2">
                            {actions.canEdit && (
                              <button
                                className="btn btn-icon"
                                title="Editar"
                                aria-label={`Editar solicitação ${request.colaboracao_id}`}
                                onClick={() =>
                                  handleEdit(request.colaboracao_id)
                                }
                              >
                                <Edit2 size={18} />
                              </button>
                            )}
                            {actions.canView && (
                              <button
                                className="btn btn-icon"
                                title="Visualizar"
                                aria-label={`Visualizar solicitação ${request.colaboracao_id}`}
                                onClick={() =>
                                  handleView(request.colaboracao_id)
                                }
                              >
                                <Eye size={18} />
                              </button>
                            )}
                            {actions.canDelete && (
                              <button
                                className="btn btn-icon text-danger"
                                title="Excluir"
                                aria-label={`Excluir solicitação ${request.colaboracao_id}`}
                                onClick={() =>
                                  handleDelete(request.colaboracao_id)
                                }
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              {!loading && !error && visibleRequests.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted text-center">
                    Nenhuma solicitação encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

            </div>
            <TablePagination page={currentPage} totalPages={totalPages} pageSize={pageSize} total={total} disabled={loading} onPageChange={setCurrentPage} onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }} />
          </section>
        </main>
      </div>

      {/* Modal de Confirmação */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem certeza que deseja excluir a solicitação #{selectedRequestId}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>


    </div>
  );
};

export default RequestsTableAdmin;
